import React, { useState, useEffect, useRef } from 'react';

interface Service {
  iconType: string;
  title: string;
  description: string;
  features: string[];
}

const ServicesSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const services: Service[] = [
    {
      iconType: 'trading',
      title: 'Orderbook Market Making',
      description: 'Providing best-in-class market depth and tight spreads across hundreds of markets, supporting both perpetual and spot trading.',
      features: ['95%+ Uptime', 'Best-in-class Depth & Spread', 'Hundreds of Markets', 'Perpetual & Spot Support']
    },
    {
      iconType: 'defi',
      title: 'DeFi Integration',
      description: 'Comprehensive DeFi solutions across major blockchain ecosystems with expert protocol integration and advisory services.',
      features: ['EVM & Solana Support', 'AMM Liquidity Management', 'Protocol Advisory', 'Cross-chain Solutions']
    },
    {
      iconType: 'arbitrage',
      title: 'Proprietary Trading',
      description: 'Low-latency trading strategies that enhance market efficiency across multiple venues and asset classes.',
      features: ['Quantitative Prop Trading Book', 'Arbitrage', 'Low Latency Execution']
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const renderIcon = (iconType: string) => {
    switch (iconType) {
      case 'trading':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#001aff" strokeWidth="1.5">
            <path d="M3 3v18h18"/>
            <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
          </svg>
        );
      case 'arbitrage':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#001aff" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
        );
      case 'defi':
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#001aff" strokeWidth="1.5">
            <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
            <path d="M8 11l2 2 4-4"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section id="services" ref={sectionRef} className="services-section">
      <div className="services-container">
        <div className="section-header">
          <h2 className={`section-title ${isVisible ? 'animate-in' : ''}`}>
            Our Services
          </h2>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <div
              key={index}
              className={`service-card ${isVisible ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="service-icon">
                {renderIcon(service.iconType)}
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection; 